import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { ResponsiveService } from '../../../shared/services/responsive.service';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { StepUpAuthService } from '../../../shared/services/step-up-auth.service';
import { CardLoaderComponent } from '../../../shared/components/card-loader/card-loader.component';
import { DownloadService } from '../../../shared/services/download.service';
import { PortletService } from '../services/portlet.service';

interface PaymentReceiptsItem {
  category: string;
  subCategory: string;
  fileName: string;
  sendDate: string;
  receivedDate: string;
  type: string;
  attachmentDocId: string;
  attachmentDocType: string;
  dmsRoles: string;
}

@Component({
  selector: 'app-payment-receipts',
  standalone: true,
  imports: [CommonModule, MatIcon, MatIconModule, ...PSIC_COMPONENTS, CardLoaderComponent],
  templateUrl: './payment-receipts.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentReceiptsComponent {
  @Input() dmsFailed: any = false;

  private readonly responsive = inject(ResponsiveService);
  private readonly store = inject(Store);
  private readonly stepUpAuth = inject(StepUpAuthService);
  private readonly downloadService = inject(DownloadService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly portletService = inject(PortletService);

  readonly isMobile = toSignal(this.responsive.isMobile$, { initialValue: false });
  readonly showMoreDataLimit = 3;

  private readonly policyNumber = this.stepUpAuth.getPolicyNumber();

  private readonly dmsSnapshot = toSignal(
    this.store.select((state: any) => state.policy?.data),
    { initialValue: null }
  );

  readonly isLoading = toSignal(
    this.store.select((state: any) => state.policy?.loading ?? false),
    { initialValue: true }
  );

  private readonly rawdocuments = computed(() => {
    const data = this.dmsSnapshot()?.[this.policyNumber]?.dmsSnapshot;
    return data && Array.isArray(data) ? data : [];
  });

  private readonly mappedPaymentReceipts = computed<PaymentReceiptsItem[]>(() =>
    this.rawdocuments().map((item: any) => ({
      category: item.category?.value ?? item.category,
      subCategory: item.subCategory?.value ?? item.subCategory,
      fileName: item.fileName?.value ?? item.fileName,
      sendDate: item.sendDate?.value ?? item.sendDate,
      receivedDate: item.receivedDate?.value ?? item.receivedDate,
      type: item.type?.value ?? item.type,
      attachmentDocId: item.attachmentDocId?.value ?? item.attachmentDocId,
      attachmentDocType: item.attachmentDocType?.value ?? item.attachmentDocType,
      dmsRoles: item.dmsRoles?.value ?? item.dmsRoles,
    }))
  );

  readonly showMoreReceipts = signal(false);
  readonly paymentReceiptsCount = computed(() => this.mappedPaymentReceipts().length);

  readonly paymentReceiptsList = computed<PaymentReceiptsItem[]>(() => {
    const list = this.mappedPaymentReceipts();
    return this.showMoreReceipts() ? list : list.slice(0, this.showMoreDataLimit);
  });

  onShowPaymentsReceipts(): void {
    this.showMoreReceipts.update((v) => !v);
  }

  downloadFile(attachmentDocId: string, fileName: string): void {
    this.downloadService.download(
      'downloadDocument',
      { attachmentDocId },
      fileName || 'document.pdf',
      this.destroyRef
    );
  }
}
